import React, { useState, useEffect } from "react";
import { HiOutlineTrendingUp, HiOutlineTrendingDown } from "react-icons/hi";
import { db } from "./firebase"; // Firestore configuration
import { 
  collection, 
  addDoc, 
  query, 
  where, 
  onSnapshot, 
  orderBy, 
  serverTimestamp 
} from "firebase/firestore";
import "./Overview.css";

const Overview = ({ userData }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [description, setDescription] = useState("");
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Hardcoded or dynamic email to check against the 'userEmail' field
  const targetEmail = userData?.userEmail || "nimal@gmail.com";

  // ✅ 1. Real-time Listener for Firestore 'payments' collection
  useEffect(() => {
    if (!targetEmail) return;

    const q = query(
      collection(db, "payments"),
      where("userEmail", "==", targetEmail),
      orderBy("timestamp", "desc")
    );

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const paymentList = [];
      querySnapshot.forEach((doc) => {
        paymentList.push({ id: doc.id, ...doc.data() });
      });
      setTransactions(paymentList);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching payments: ", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [targetEmail]);

  // ✅ 2. Prevent background scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = isModalOpen ? "hidden" : "unset";
    return () => { document.body.style.overflow = "unset"; };
  }, [isModalOpen]);

  const showToast = (message, type = "success") => {
    const container = document.getElementById("toast-container");
    if (!container) return;
    const toast = document.createElement("div");
    toast.classList.add("toast");
    toast.textContent = message;
    if (type === "error") toast.style.background = "#ef4444";
    else if (type === "info") toast.style.background = "#3b82f6";
    else toast.style.background = "#16a34a";
    container.appendChild(toast);
    setTimeout(() => toast.remove(), 6000);
  };

  const calculateGrowth = () => {
    const current = userData?.revenue || 0;
    const previous = userData?.previousRevenue || 0;
    if (previous === 0) return current > 0 ? 100 : 0;
    return (((current - previous) / previous) * 100).toFixed(1);
  };
  
  const growthValue = calculateGrowth();
  const isPositive = growthValue >= 0;

  const formatCurrency = (amount) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount || 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await addDoc(collection(db, "contacts"), {
        email: targetEmail,
        username: userData?.name || "",
        description,
        createdAt: serverTimestamp(),
      });
      showToast("Issue submitted successfully!", "success");
      setDescription("");
      setIsModalOpen(false);
    } catch (error) {
      showToast("Failed to submit. Please try again.", "error");
    }
  };

  return (
    <section className="view-wrapper">
      <div id="toast-container" style={{ position: "fixed", top: 20, right: 20, zIndex: 9999 }}></div>

      <div className="view-header">
        <div>
          <h1>Dashboard Overview</h1>
          <p>Welcome back! Managing data for: <strong>{targetEmail}</strong></p>
        </div>
        <button className="action-primary" onClick={() => setIsModalOpen(true)}>
          + Contact Us
        </button>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={() => setIsModalOpen(false)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-header">Contact Support</h2>
            <form onSubmit={handleSubmit}>
              <label>User Email</label>
              <input type="email" value={targetEmail} readOnly />
              <label>Issue Description</label>
              <textarea
                placeholder="Describe your issue..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                required
              ></textarea>
              <div className="modal-actions">
                <button type="submit" className="action-primary">Submit</button>
                <button type="button" className="action-secondary" onClick={() => setIsModalOpen(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="stats-grid-v2">
        <div className="stat-card-v2 gradient-card">
          <div className="card-top">
            <span className="label-light">Wallet Balance</span>
            {isPositive ? <HiOutlineTrendingUp className="trend-icon" /> : <HiOutlineTrendingDown className="trend-icon" />}
          </div>
          <div className="card-body">
            <h2>{formatCurrency(userData?.revenue)}</h2>
            <div className={`growth-tag ${isPositive ? "pos" : "neg"}`}>
              {isPositive ? "+" : ""}{growthValue}% from last month
            </div>
          </div>
        </div>

        <div className="stat-card-v2">
          <span className="label-gray">Account Status</span>
          <div className={`status-indicator ${userData?.status?.toLowerCase()}`}>
            {userData?.status || "Pending"}
          </div>
          <p className="sub-text">Account verified</p>
        </div>

        <div className="stat-card-v2">
          <span className="label-gray">WEBSITE</span>
          <p className="domain-text">{userData?.domain || "Pending Deployment"}</p>
          {userData?.url && (
            <a href={userData.url} target="_blank" rel="noopener noreferrer" className="link-btn">
             Login to Site →
            </a>
          )}
        </div>
      </div>

      {/* Recent Activity Table */}
      <div className="activity-section">
        <h3>Recent Activity (Live)</h3>
        <div className="activity-table-card">
          {/* Header Row */}
          <div className="table-row head">
            <span>Withdrawal Type</span>
            <span>Date</span>
            <span>Amount</span>
            <span>Status</span>
          </div>

          {loading ? (
            <div className="table-row-loading"><span>Loading transactions...</span></div>
          ) : transactions.length > 0 ? (
            transactions.map((item) => (
              <div className="table-row" key={item.id}>
                <span className="col-text">{item.type || item.withdrawalType || "Withdrawal"}</span>
                <span className="col-text">
                  {item.timestamp?.seconds 
  ? new Date(item.timestamp.seconds * 1000).toLocaleDateString() 
  : "Pending..."}
                </span>
                <span className="amt-neg">
                  -{formatCurrency(item.amount)}
                </span>
                {/* Status Column */}
                <div className="status-col">
                    <span className="status-tag-green">
                    {item.status || "Completed"}
                    </span>
                </div>
              </div>
            ))
          ) : (
            <div className="table-empty-state">
              <span>No recent transactions found for this user.</span>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Overview;